/**
 * Migraciones de forma de datos guardados (borradores, snapshots, JSON viejos) a la forma vigente.
 * Puras e idempotentes: se corren en cada lectura y en el preprocess de zod, así que un JSON ya migrado sale igual.
 *
 * Historial:
 *  - 22-oct-2026 · diagnóstico rehecho (war room 19-oct): pasos 1 y 2 con 6 preguntas cada uno y la Rueda 360 con 12
 *    preguntas, contestados en la app. Se van `final`, `wheelFri`, `wheelSun` y `reflections` (eran del evento de WealthX);
 *    `s1Avg`/`s2Avg` y `wheelAvg` quedan como LEGADO del Excel WX. El área "Espiritual" pasa a "Emocional".
 *  - 2-nov-2026 · la app deja de guardar nombres de personas (pedido de Pablo tras la revisión externa): `setup.name` siempre null.
 */
import type { ModelInputs, Num, Snapshot } from "./types";

const esObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const num = (v: unknown): Num => (typeof v === "number" && Number.isFinite(v) ? v : null);
/** Arreglo de exactamente `n` números-o-null (recorta o rellena con null). */
const fit = (v: unknown, n: number): Num[] => {
  const src = Array.isArray(v) ? v : [];
  return Array.from({ length: n }, (_, k) => num(src[k]));
};

export const DIAG_LEN = { s1: 6, s2: 6, s3: 4, s4: 4, s5: 4, wheel: 4, wheelAvg: 3 } as const;

/** `diag` en la forma vigente a partir de cualquier forma anterior. */
export function migrarDiag(raw: unknown): ModelInputs["diag"] {
  const d = esObj(raw) ? raw : {};
  const w = esObj(d.wheel) ? d.wheel : {};
  // Rueda del Excel WX: antes venía como wheelFri/wheelSun (viernes/domingo por área); se conserva solo el viernes como legado.
  const legado = Array.isArray(d.wheelAvg) ? d.wheelAvg : Array.isArray(d.wheelFri) ? d.wheelFri : [];
  return {
    answeredAt: typeof d.answeredAt === "string" && d.answeredAt ? d.answeredAt : null,
    s1: fit(d.s1, DIAG_LEN.s1), s2: fit(d.s2, DIAG_LEN.s2), s3: fit(d.s3, DIAG_LEN.s3), s4: fit(d.s4, DIAG_LEN.s4), s5: fit(d.s5, DIAG_LEN.s5),
    s1Avg: num(d.s1Avg), s2Avg: num(d.s2Avg),
    wheel: { fisica: fit(w.fisica, DIAG_LEN.wheel), emocional: fit(w.emocional ?? w.espiritual, DIAG_LEN.wheel), relaciones: fit(w.relaciones, DIAG_LEN.wheel) },
    wheelAvg: fit(legado, DIAG_LEN.wheelAvg),
  };
}

/**
 * ModelInputs guardados (cualquier versión de forma) → forma vigente. No muta el original. Si `raw` no es un objeto lo
 * devuelve tal cual para que zod reporte el error correcto.
 */
export function migrarInputs<T>(raw: T): T {
  if (!esObj(raw)) return raw;
  const out: Record<string, unknown> = { ...raw, diag: migrarDiag(raw.diag) };
  // 2-nov-2026 · la app no guarda nombres: lo que venga en setup.name (B6 del Excel, borradores viejos, localStorage) se descarta.
  if (esObj(raw.setup)) out.setup = { ...raw.setup, name: null };
  if (esObj(raw.system) && esObj(raw.system.courage) && raw.system.courage.area === "Espiritual") {
    out.system = { ...raw.system, courage: { ...raw.system.courage, area: "Emocional" } };
  }
  return out as T;
}

const WHEEL_META: Pick<Snapshot["idp"]["wheel"][number], "key" | "area" | "label">[] = [
  { key: "fisica", area: "Física", label: "Riqueza Física" },
  { key: "emocional", area: "Emocional", label: "Riqueza Emocional" },
  { key: "relaciones", area: "Relaciones", label: "Riqueza de Relaciones" },
];

/** `snapshot.idp` guardado con la forma anterior (initial/final, fri/sun) → forma vigente. Ya migrado = tal cual. */
export function migrarIdp(raw: unknown): Snapshot["idp"] {
  const o = esObj(raw) ? raw : {};
  if ("score" in o && Array.isArray(o.steps)) return o as unknown as Snapshot["idp"];
  const steps = Array.isArray(o.steps) ? o.steps : [];
  const sa = esObj(o.stepAverages) ? o.stepAverages : {};
  const st = (key: Snapshot["idp"]["steps"][number]["key"], label: string) => {
    const s = steps.find((x) => esObj(x) && x.key === key) as Record<string, unknown> | undefined;
    const value = num(s?.initial);
    return { key, label, value, legacy: value !== null && (key === "s1" || key === "s2") };
  };
  const stepsOut = [st("s1", "1 · Mi identidad patrimonial"), st("s2", "2 · Mi vida patrimonial"), st("s3", "3 · Mi realidad patrimonial"), st("s4", "4 · Mi estrategia patrimonial"), st("s5", "5 · Mi sistema patrimonial")];
  const wheelOld = Array.isArray(o.wheel) ? o.wheel : [];
  const wheel = WHEEL_META.map((m, i) => {
    const w = wheelOld[i] as Record<string, unknown> | undefined;
    const value = num(w?.fri);
    return { ...m, value, legacy: value !== null };
  });
  const ir = esObj(o.ir360) ? num(o.ir360.fri) : num(o.ir360);
  return {
    answeredAt: null,
    stepAverages: { s1: stepsOut[0].value, s2: stepsOut[1].value, s3: num(sa.s3), s4: num(sa.s4), s5: num(sa.s5) },
    steps: stepsOut,
    score: num(o.initial),
    wheel,
    ir360: ir,
  };
}

/** Snapshot leído de la base → con `idp` en la forma vigente. */
export function migrarSnapshot(s: Snapshot): Snapshot {
  const idp = migrarIdp((s as unknown as { idp: unknown }).idp);
  return idp === (s as unknown as { idp: unknown }).idp ? s : { ...s, idp };
}
